import { createContext, type ReactNode, useContext } from "react";

/**
 * Carries the active split-view panel's query string (e.g. "?warrantyId=1&jobId=2")
 * down to descendant launchers so child-modal navigation can preserve it.
 *
 * - `undefined` → NOT inside a panel. `usePanelAwareNavigate` no-ops (empty suffix),
 *   keeping navigation byte-identical to non-panel behavior.
 * - "" or "?..." → inside a panel; the value is the panel's current `location.search`.
 *
 * Provided ONLY by a panel host (e.g. the Warranties detail slot), never by
 * `BTSplitView` itself — other split-view consumers must stay unaffected.
 */
const PanelNavContext = createContext<string | undefined>(undefined);

interface IPanelNavProviderProps {
  /** The panel's current query string — pass `location.search` from the host. */
  search: string;
  children: ReactNode;
}

export const PanelNavProvider = ({
  search,
  children,
}: IPanelNavProviderProps) => (
  <PanelNavContext.Provider value={search}>{children}</PanelNavContext.Provider>
);

/** Returns the panel query string, or `undefined` when not inside a panel. */
export const usePanelNavContext = (): string | undefined =>
  useContext(PanelNavContext);
